import type {Author} from "~/graphql/__generated__/graphql"
import {usePersistedStore} from "~/stores/usePersistedStore"
import type {Ref} from "vue";
import NotFoundError from "~/libs/errors/NotFoundError"
import type AuthorLocal from "~/libs/interfaces/AuthorLocal"
import type AuthorFilters from "~/libs/interfaces/AuthorFilters"
import {GET_AUTHORS} from "~/graphql/authors"
import authorLocalFactory from "~/libs/factories/AuthorLocalFactory"
import {AuthorStoreType} from "~/libs/constants/AuthorStoreType"

export enum AUTHOR_QUERY_TYPE {
  PREVIEW = 'preview',
  DETAIL = 'detail'
}

const authorStore = (type: AuthorStoreType = AuthorStoreType.AUTHOR, key?: string) => defineStore(`authors-${type}-${key}`, {
  state: () => {
    const state: {
      isLoading: Ref<boolean>
      limit: Ref<number>
      authors: AuthorLocal[]
      author: Ref<AuthorLocal | null>
      filters: AuthorFilters
    } = {
      isLoading: ref(true),
      limit: ref(10),
      authors: reactive([]),
      author: ref(null),
      filters: {},
    }
    return state
  },
  getters: {
    getIsLoading(state): boolean {
      return state.isLoading
    }
  },
  actions: {
    setLimit(limit: number) {
      this.limit = limit
    },
    /**
     * Get authors with applied filters and presets
     * @param offset
     * @param limit
     * @return AuthorLocal[]
     */
    getFilteredAuthors(offset: number = 0, limit: number = 10): AuthorLocal[] {
      let matchedCount = 0

      const authors = this.authors.map((author) => {
        let matchFilters = true

        if (matchFilters) {
          matchedCount = matchedCount + 1
        }

        if (matchFilters && matchedCount <= offset) {
          matchFilters = false
        } else if (matchFilters && matchedCount > (offset + limit)) {
          matchFilters = false
        }

        return {
          ...author,
          isVisible: matchFilters
        }
      })
      return authors
    },

    /**
     * Get only filtered authors (remove non visible)
     * @param offset
     * @param limit
     */
    getOnlyFilteredAuthors(offset: number = 0, limit: number = 10): AuthorLocal[] {
      const authors = this.getFilteredAuthors(offset, limit)
      return authors.filter((author) => author.isVisible)
    },

    /**
     * Get count of getFilteredAuthors
     * @return number
     */
    getFilteredAuthorsCount(): number {
      const authors = this.getFilteredAuthors(0, this.authors.length).filter(author => author.isVisible)
      return authors.length
    },
    /**
     * Get author by id
     * @param id
     */
    getAuthor(id: string): AuthorLocal | undefined {
      const author = this.authors.find((author) => {
        return author.id.indexOf(id) !== -1
      })
      return author
    },
    /**
     * Fetch filter changed
     * @param filters
     */
    filterChanged(filters?: AuthorFilters): boolean {
      const sameArrays = (a?: string[], b?: string[]) => {
        if ((!a && b) || (a && !b)) {
          return false
        }
        if (!a && !b) {
          return true
        }
        if (a === b) {
          return true
        }
        if (a && b) {
          if (a.length !== b.length) {
            return false
          }
          const sortedA = a.sort()
          const sortedB = b.sort()

          for (let i = 0; i < sortedA.length; i++) {
            if (sortedA[i] !== sortedB[i]) {
              return false
            }
          }
        }
        return true
      }

      // check ids filter
      if (!sameArrays(filters?.ids, this.filters.ids)) {
        return true
      }

      // check ids filter
      if (!sameArrays(filters?.originIdList, this.filters.originIdList)) {
        return true
      }

      // check fulltext
      if (filters?.fulltext !== this.filters.fulltext) {
        return true
      }

      return false
    },
    checkFiltersChanges(filters: AuthorFilters): void {
      // reset authors if filter changed
      if (this.filterChanged(filters)) {
        this.filters.ids = filters.ids
        this.filters.originIdList = filters.originIdList
        this.filters.fulltext = filters.fulltext
        this.authors = reactive([])
        this.author = null
      }
    },

    /**
     * Fetch authors from server
     * @return {Promise<void>}
     */
    async fetchAuthors(filters: AuthorFilters = {}, queryType:AUTHOR_QUERY_TYPE = AUTHOR_QUERY_TYPE.DETAIL, forceRefetch: boolean = false): Promise<void> {
      const logger = useFrontendLogger()
      const config = useRuntimeConfig()
      const apolloContext = {
        fetchOptions: { signal: AbortSignal.timeout(Number(config.public.authorsStoreTimeout || 5000)) }
      }
      const persistedStore = usePersistedStore()
      let getAuthorDetail = false

      let query = GET_AUTHORS
      switch (queryType) {
        case AUTHOR_QUERY_TYPE.DETAIL:
          getAuthorDetail = true
          query = GET_AUTHORS
          break
        case AUTHOR_QUERY_TYPE.PREVIEW:
        default:
          getAuthorDetail = false
          query = GET_AUTHORS
          break
      }

      // reset authors if filter changed
      this.checkFiltersChanges(filters)

      // no need to fetch authors
      if (!forceRefetch && this.authors.length >= this.limit && !persistedStore.needSync(this.$id)) {
        return
      }

      this.isLoading = true
      const filter = {
        first: this.limit,
        isDeleted: false,
        // isCompleted: true,
        ...(filters && {
          // ids
          ...(filters.ids?.length && {
            idList: filters.ids
          }),
          // origin ids
          ...(filters.originIdList?.length && {
            originIdList: filters.originIdList
          }),
          // fulltext
          ...(filters.fulltext && filters.fulltext.length && {
            fullname: filters.fulltext
          })
        })
      }

      // SSR server side initial data fetch
      if (import.meta.server) {
        try {
          const result = await useAsyncQuery(query, filter, undefined, apolloContext)
          if (result.error?.value) {
            logger?.error('useAuthorsStore:fetchAuthors:server - error', {
              errorDetail: result.error?.value,
              getAuthorsQueryType: queryType,
              getAuthorsFilter: filter,
            })
          }
          //@ts-ignore
          const edges = result.data?.value?.authors?.edges || []
          this.processAuthorsData(edges, getAuthorDetail)
          this.isLoading = false
        } catch (e) {
          this.isLoading = false
          logger?.error('useAuthorsStore:fetchAuthors:server - exception', {
            errorDetail: e,
            getAuthorsQueryType: queryType,
            getAuthorsFilter: filter
          })
          throw e
        }
        persistedStore.setLastSync(this.$id)
      } else {
        // client interaction data fetch
        return new Promise((resolve, reject) => {
          try {
            const options = {
              context: apolloContext
            }
            const {onResult, onError} = useQuery(query, filter, options)
            onResult((result) => {
              if (!result.loading) {
                this.isLoading = result.loading
                persistedStore.setLastSync(this.$id)
                const edges = result.data?.authors?.edges || []
                try {
                  this.processAuthorsData(edges, getAuthorDetail)
                  resolve()
                } catch (e) {
                  this.isLoading = false
                  logger?.error('useAuthorsStore:fetchAuthors:client - processAuthorsData exception', {
                    errorDetail: e,
                    getAuthorsQueryType: queryType,
                    getAuthorsFilter: filter
                  })
                  reject(e)
                }
              }
            })
            onError((error) => {
              this.isLoading = false
              logger?.error('useAuthorsStore:fetchAuthors:client - error', {
                errorDetail: error,
                getAuthorsQueryType: queryType,
                getAuthorsFilter: filter
              })
              reject(error)
            })
          } catch (e) {
            this.isLoading = false
            logger?.error('useAuthorsStore:fetchAuthors:client - exception', {
              errorDetail: e,
              getAuthorsQueryType: queryType,
              getAuthorsFilter: filter
            })
            reject(e)
          }
        })
      }
    },
    /**
     * Process loaded authors from GQL
     * @param data
     * @param isDetail
     */
    processAuthorsData(data: any[], isDetail: boolean = false):void {
      if (data.length) {
        const newAuthors: Author[] = data.map((item) => item?.node).filter(Boolean)
        // check author detail load
        if (isDetail && newAuthors[0]) {
          this.setAuthor(newAuthors[0])
        } else if (isDetail && (!newAuthors || !newAuthors.length)) {
          throw new NotFoundError('Author not found')
        }
        if (newAuthors && newAuthors.length) {
          // add new authors to author list
          this.addAuthors(newAuthors, true)
        }
      } else if (isDetail) {
        throw new NotFoundError('Author not found')
      }
    },
    /**
     * Add authors to store - remove duplicities
     * @param newAuthors
     * @param rewrite
     */
    addAuthors(newAuthors: Author[], rewrite: boolean = false): void {
      newAuthors.forEach((newAuthor) => {
        const authorLocal = authorLocalFactory.createAuthorLocal(newAuthor)
        const foundIndex = this.authors.findIndex(author => author.id === newAuthor.id)
        if (foundIndex >= 0 && rewrite) {
          this.authors.splice(foundIndex, 1)
        }
        if (foundIndex === -1 || rewrite) {
          this.authors.push(authorLocal)
        }
      })

      // sort authors
      this.authors.sort((a, b) => {
        if (a.fullname && b.fullname) {
          return a.fullname.localeCompare(b.fullname);
        }
        return 1
      })
    },

    setAuthor(newAuthor: Author): void {
      this.author = authorLocalFactory.createAuthorLocal(newAuthor)
    }

  }
})

export const useAuthorsStore = (type: AuthorStoreType, key?: string) => {
  return authorStore(type, key)()
}
